// Invite Borrower (ticket 192): email the borrower's deal link, or copy it.
// Shared by the loan header and sidebar and the Deals page (all take the deal id,
// a loan file id or a transaction id); each needs `links.issue`.
import { useState } from 'react';
import { losApi } from '../../losApi';
import { useAuth } from '../../AuthContext';
import { copyText } from '../../clipboard';
import { errorMessage } from '../../errors';
import { toast } from '../../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';

const NOT_SENT = {
  provider_not_configured: 'Email is not set up for this workspace.',
  frontend_url_not_set: 'The app address is not configured, so the link cannot be built.',
  send_failed: 'The email service refused it.',
};

const whyNotSent = (reason) => (
  reason?.startsWith('recipient_blocked') ? 'That address is blocked from receiving email.' : NOT_SENT[reason] || 'It could not be sent.'
);

export function useBorrowerInvite(dealId) {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const canInvite = (user?.permissions || []).includes('links.issue');

  const send = async () => {
    // A real email to the client: one stray click must not send it.
    if (!(await confirmDialog({
      title: 'Email the borrower their loan link?',
      description: "This sends an email to the borrower's address on file.",
      confirmText: 'Send invite',
    }))) return;
    setSending(true);
    try {
      const r = await losApi.sendBorrowerLink(dealId);
      if (r.email_sent) toast({ title: 'Invite sent', description: `Link emailed to ${r.sent_to}` });
      else toast({ title: 'Invite not sent', description: `${whyNotSent(r.reason)} Use Copy link instead.` });
    } catch (e) {
      toast({ title: 'Invite not sent', description: errorMessage(e) });
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try {
      const link = await losApi.getBorrowerLink(dealId);
      if (await copyText(`${window.location.origin}${link.url_path}`)) toast({ title: 'Borrower link copied' });
      else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
    } catch (e) {
      toast({ title: 'Could not get the borrower link', description: errorMessage(e) });
    }
  };

  return { canInvite, sending, send, copyLink };
}
